import { useState, useEffect } from 'react';
import { getSamples, getCustomers, createSample, returnSample, updateSample, createCustomer } from '../services/api';
import CustomDropdown from './CustomDropdown';
import { useResponsive } from '../hooks/useResponsive';

function Samples() {
  const [samples, setSamples] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingSampleId, setEditingSampleId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');

  const { isMobile } = useResponsive();
  const [formData, setFormData] = useState({
    customerId: '',
    productName: '',
    specification: '',
    quantity: '',
    unit: 'pcs',
    promisedReturnDate: '',
    remarks: ''
  });
  const [editFormData, setEditFormData] = useState({
    customerId: '',
    productName: '',
    specification: '',
    quantity: '',
    unit: 'pcs',
    promisedReturnDate: '',
    remarks: ''
  });

  useEffect(() => {
    loadSamples();
    loadCustomers();
  }, []);

  const loadSamples = async () => {
    try {
      const data = await getSamples();
      setSamples(data);
    } catch (error) {
      alert('Error loading samples: ' + error.message);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleCreateCustomer = async (customerName) => {
    try {
      const newCustomer = await createCustomer({
        name: customerName,
        contactPerson: '',
        phone: '',
        email: '',
        address: ''
      });
      // Reload customers and select the newly created one
      await loadCustomers();
      setFormData({ ...formData, customerId: newCustomer.id });
      return newCustomer;
    } catch (error) {
      alert('Error creating customer: ' + error.message);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createSample(formData);
      setShowForm(false);
      setFormData({
        customerId: '',
        productName: '',
        specification: '',
        quantity: '',
        unit: 'pcs',
        promisedReturnDate: '',
        remarks: ''
      });
      loadSamples();
    } catch (error) {
      alert('Error creating sample: ' + error.message);
    }
  };

  const handleReturn = async (id) => {
    if (window.confirm('Mark this sample as returned?')) {
      try {
        await returnSample(id, { actualReturnDate: new Date().toISOString() });
        loadSamples();
      } catch (error) {
        alert('Error marking sample as returned: ' + error.message);
      }
    }
  };

  const handleEdit = (sample) => {
    setEditingSampleId(sample.id);
    setEditFormData({
      customerId: sample.customerId,
      productName: sample.productName,
      specification: sample.specification || '',
      quantity: sample.quantity,
      unit: sample.unit,
      promisedReturnDate: sample.promisedReturnDate ? sample.promisedReturnDate.split('T')[0] : '',
      remarks: sample.remarks || ''
    });
    setShowEditForm(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSample(editingSampleId, editFormData);
      setShowEditForm(false);
      setEditingSampleId(null);
      setEditFormData({
        customerId: '',
        productName: '',
        specification: '',
        quantity: '',
        unit: 'pcs',
        promisedReturnDate: '',
        remarks: ''
      });
      loadSamples();
    } catch (error) {
      alert('Error updating sample: ' + error.message);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getStatusBadge = (status) => {
    const colors = {
      issued: '#3498db',
      delayed: '#f39c12',
      returned: '#27ae60'
    };

    return (
      <span style={{
        padding: '4px 12px',
        background: colors[status] || '#95a5a6',
        color: 'white',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getFilteredSamples = () => {
    let filtered = samples;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(sample => {
        const customerName = getCustomerName(sample.customerId).toLowerCase();
        const productName = (sample.productName || '').toLowerCase();
        const specification = (sample.specification || '').toLowerCase();
        const sampleNumber = (sample.sampleNumber || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        return customerName.includes(searchLower) ||
               productName.includes(searchLower) ||
               specification.includes(searchLower) ||
               sampleNumber.includes(searchLower);
      });
    }

    // Month filter
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(sample => {
        const sampleDate = new Date(sample.issuedDate);
        const [year, month] = selectedMonth.split('-');
        return sampleDate.getFullYear() === parseInt(year) &&
               sampleDate.getMonth() === parseInt(month) - 1;
      });
    }

    return filtered;
  };

  const filteredSamples = getFilteredSamples();

  const inputStyle = {
    width: '100%',
    padding: '8px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    padding: '10px 20px',
    background: '#C8342B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    marginRight: '10px',
    fontWeight: '500'
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#3D1F1F' }}>Sample Tracking</h1>
        <button onClick={() => setShowForm(true)} style={buttonStyle}>
          Issue Sample
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <div style={{ flex: isMobile ? '1 1 100%' : 2 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search by sample number, customer, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Month
          </label>
          <CustomDropdown
            value={selectedMonth}
            onChange={(value) => setSelectedMonth(value)}
            options={[
              { value: 'all', label: 'All Months' },
              ...(() => {
                const months = [];
                const now = new Date();
                for (let i = 0; i < 12; i++) {
                  const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  months.push({ value: `${year}-${month}`, label });
                }
                return months;
              })()
            ]}
            placeholder="All Months"
          />
        </div>
      </div>

      {/* Sample Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '550px',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
            <h2 style={{ marginTop: 0 }}>Issue New Sample</h2>
            <form onSubmit={handleSubmit} style={{ width: '100%', boxSizing: 'border-box' }}>
              <CustomDropdown
                value={formData.customerId}
                onChange={(value) => setFormData({ ...formData, customerId: value })}
                options={customers.map(customer => ({
                  value: customer.id,
                  label: customer.name
                }))}
                placeholder="Select Customer *"
                style={{ marginBottom: '10px', width: '100%', boxSizing: 'border-box' }}
                required
                creatable
                onCreate={handleCreateCustomer}
                createLabel="Create customer"
              />

              <input
                style={inputStyle}
                type="text"
                placeholder="Product Name *"
                value={formData.productName}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                required
              />

              <input
                style={inputStyle}
                type="text"
                placeholder="Specification"
                value={formData.specification}
                onChange={(e) => setFormData({ ...formData, specification: e.target.value })}
              />

              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '10px',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="Quantity *"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                />
                <div style={{ width: '100%', boxSizing: 'border-box' }}>
                  <CustomDropdown
                    value={formData.unit}
                    onChange={(value) => setFormData({ ...formData, unit: value })}
                    options={[
                      { value: 'pcs', label: 'Pcs' },
                      { value: 'sheets', label: 'Sheets' }
                    ]}
                    placeholder="Unit"
                    style={{ marginBottom: '0' }}
                  />
                </div>
              </div>

              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#7f8c8d' }}>
                Promised Return Date
              </label>
              <input
                style={inputStyle}
                type="date"
                value={formData.promisedReturnDate}
                onChange={(e) => setFormData({ ...formData, promisedReturnDate: e.target.value })}
              />

              <textarea
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                placeholder="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              />

              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '5px',
                paddingTop: '15px',
                borderTop: '1px solid #ecf0f1',
                flexWrap: 'wrap'
              }}>
                <button type="submit" style={buttonStyle}>
                  Issue Sample
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{ ...buttonStyle, background: '#95a5a6' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Sample Form Modal */}
      {showEditForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '550px',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
            <h2 style={{ marginTop: 0 }}>Edit Sample</h2>
            <form onSubmit={handleEditSubmit} style={{ width: '100%', boxSizing: 'border-box' }}>
              <CustomDropdown
                value={editFormData.customerId}
                onChange={(value) => setEditFormData({ ...editFormData, customerId: value })}
                options={customers.map(customer => ({
                  value: customer.id,
                  label: customer.name
                }))}
                placeholder="Select Customer *"
                style={{ marginBottom: '10px', width: '100%', boxSizing: 'border-box' }}
                required
                creatable
                onCreate={handleCreateCustomer}
                createLabel="Create customer"
              />

              <input
                style={inputStyle}
                type="text"
                placeholder="Product Name *"
                value={editFormData.productName}
                onChange={(e) => setEditFormData({ ...editFormData, productName: e.target.value })}
                required
              />

              <input
                style={inputStyle}
                type="text"
                placeholder="Specification"
                value={editFormData.specification}
                onChange={(e) => setEditFormData({ ...editFormData, specification: e.target.value })}
              />

              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '10px',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="Quantity *"
                  value={editFormData.quantity}
                  onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                  required
                />
                <div style={{ width: '100%', boxSizing: 'border-box' }}>
                  <CustomDropdown
                    value={editFormData.unit}
                    onChange={(value) => setEditFormData({ ...editFormData, unit: value })}
                    options={[
                      { value: 'pcs', label: 'Pcs' },
                      { value: 'sheets', label: 'Sheets' }
                    ]}
                    placeholder="Unit"
                    style={{ marginBottom: '0' }}
                  />
                </div>
              </div>

              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#7f8c8d' }}>
                Promised Return Date
              </label>
              <input
                style={inputStyle}
                type="date"
                value={editFormData.promisedReturnDate}
                onChange={(e) => setEditFormData({ ...editFormData, promisedReturnDate: e.target.value })}
              />

              <textarea
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                placeholder="Remarks"
                value={editFormData.remarks}
                onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
              />

              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '5px',
                paddingTop: '15px',
                borderTop: '1px solid #ecf0f1',
                flexWrap: 'wrap'
              }}>
                <button type="submit" style={buttonStyle}>
                  Update Sample
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  style={{ ...buttonStyle, background: '#95a5a6' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {samples.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ color: '#7f8c8d', fontSize: '16px' }}>No samples found. Issue samples to customers for evaluation.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: '20px'
        }}>
          {filteredSamples.map(sample => (
            <div
              key={sample.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Header with Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Customer</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#3D1F1F' }}>
                    {getCustomerName(sample.customerId)}
                  </div>
                </div>
                {getStatusBadge(sample.status)}
              </div>

              {/* Product Details */}
              <div style={{
                background: '#f8f9fa',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '6px' }}>Product</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', marginBottom: '4px' }}>
                  {sample.productName}
                </div>
                {sample.specification && (
                  <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                    {sample.specification}
                  </div>
                )}
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#27ae60', marginTop: '8px' }}>
                  {sample.quantity} {sample.unit}
                </div>
              </div>

              {/* Dates Section */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
                marginBottom: '12px'
              }}>
                <div style={{
                  background: '#f8f9fa',
                  padding: '10px',
                  borderRadius: '6px'
                }}>
                  <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '4px' }}>Issued Date</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>
                    {new Date(sample.issuedDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
                <div style={{
                  background: '#f8f9fa',
                  padding: '10px',
                  borderRadius: '6px'
                }}>
                  <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '4px' }}>Promised Return</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>
                    {sample.promisedReturnDate
                      ? new Date(sample.promisedReturnDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Actual Return Date */}
              {sample.actualReturnDate && (
                <div style={{
                  background: '#d4edda',
                  padding: '10px',
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '11px', color: '#155724', marginBottom: '4px' }}>Returned On</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#155724' }}>
                    {new Date(sample.actualReturnDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              )}

              {/* Remarks */}
              {sample.remarks && (
                <div style={{
                  background: '#fff3cd',
                  padding: '10px',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: '#856404'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px' }}>Remarks</div>
                  {sample.remarks}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: (sample.status === 'issued' || sample.status === 'delayed') ? '0' : '12px'
              }}>
                {/* Edit Button - Available for all samples except returned */}
                {sample.status !== 'returned' && (
                  <button
                    onClick={() => handleEdit(sample)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      background: '#3498db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(52, 152, 219, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#2980b9';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#3498db';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(52, 152, 219, 0.3)';
                    }}
                  >
                    Edit
                  </button>
                )}

                {/* Mark as Returned Button */}
                {(sample.status === 'issued' || sample.status === 'delayed') && (
                  <button
                    onClick={() => handleReturn(sample.id)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      background: '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(39, 174, 96, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#229954';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#27ae60';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(39, 174, 96, 0.3)';
                    }}
                  >
                    Mark as Returned
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Samples;
